import { useEffect } from "react";
import { ArrowLeft, Building2, LayoutTemplate, Tags, Users } from "lucide-react";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { toast } from "sonner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const PLATFORM_ROLE_LABELS: Record<string, string> = {
  operator: "运营",
  admin: "管理员",
  superadmin: "超级管理员",
};

function formatPlatformRole(role: string | null) {
  if (!role) return "—";
  return PLATFORM_ROLE_LABELS[role] ?? role;
}

const NAV_ITEMS = [
  { title: "用户", url: "/platform/users", icon: Users },
  { title: "账户", url: "/platform/accounts", icon: Building2 },
  { title: "品牌", url: "/platform/brands", icon: Tags },
  { title: "模板", url: "/platform/templates", icon: LayoutTemplate },
] as const;

export function PlatformShell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isTemplateIde = /^\/platform\/templates\/[^/]+\/ide\/?$/.test(pathname);

  useEffect(() => {
    if (user && !user.platform_role) {
      toast.error("无权访问平台管理");
    }
  }, [user]);

  if (!user?.platform_role) {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarProvider className="h-svh max-h-svh overflow-hidden">
      <Sidebar variant="inset" className="border-r">
        <SidebarHeader className="border-b border-border/60 px-4 py-4">
          <p className="text-sm font-semibold">平台管理</p>
          <p className="text-xs text-muted-foreground">运营控制台 · {formatPlatformRole(user.platform_role)}</p>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>目录</SidebarGroupLabel>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={pathname === item.url || pathname.startsWith(`${item.url}/`)}>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="border-t border-border/60 p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link to="/" className={cn("text-muted-foreground")}>
                  <ArrowLeft />
                  <span>返回工作台</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <main
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            isTemplateIde ? "overflow-hidden p-0" : "overflow-auto p-6 md:p-8",
          )}
        >
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
